import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import api from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Poptávka byla odeslána. Brzy se ozveme!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Nepodařilo se odeslat. Zkuste to prosím znovu.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32 pb-24 relative z-10 min-h-screen">
      <Seo title="Kontakt" description="Kontaktujte M + J s.r.o. — alu kola, pneumatiky, pokličky a autodíly. Telefon +420 777 181 818, rozvoz po Praze." />
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="font-head uppercase tracking-[0.3em] text-xs text-[#ff3b30] mb-4">Spojte se s námi</div>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.85] mb-14">KONTAKT</h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="space-y-8">
            <Info icon={<MapPin />} title="Adresa" lines={["M + J s.r.o.", "Truhlářská 893, 251 68", "Kamenice - Těptín"]} />
            <Info icon={<Phone />} title="Telefon" lines={[<a key="t" href="tel:+420777181818" className="hover:text-[#ff3b30]">+420 777 181 818</a>]} />
            <Info icon={<Mail />} title="E-mail" lines={[<a key="e" href="mailto:alukola-bazar@seznam.cz" className="hover:text-[#ff3b30]">alukola-bazar@seznam.cz</a>]} />
            <Info icon={<Clock />} title="Otevírací doba" lines={["Po telefonické dohodě", "Pondělí – Neděle: 7:00 – 23:00"]} />
          </div>

          <div className="border border-white/10 bg-[#0d0d0d] p-7 md:p-10">
            <h2 className="font-display text-3xl mb-6">POSLAT POPTÁVKU</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input required data-testid="contact-name" placeholder="Jméno *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
              <input required type="email" data-testid="contact-email" placeholder="E-mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
              <input data-testid="contact-phone" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
              <textarea required rows={6} data-testid="contact-message" placeholder="Co sháníte? Napište nám… *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30] resize-none" />
              <button disabled={sending} data-testid="contact-submit" className="w-full font-head uppercase tracking-wider bg-[#ff3b30] text-white py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50">
                {sending ? "Odesílání…" : "Odeslat poptávku"}
              </button>
              <p className="text-white/40 text-xs leading-relaxed" data-testid="contact-privacy-note">
                Odesláním formuláře souhlasíte se zpracováním osobních údajů za účelem vyřízení poptávky. Více v{" "}
                <Link to="/ochrana-osobnich-udaju" className="text-[#ff3b30] hover:underline">Zásadách ochrany osobních údajů</Link>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon, title, lines }) => (
  <div className="flex gap-5 border-b border-white/10 pb-8">
    <div className="text-[#ff3b30] mt-1">{icon}</div>
    <div>
      <h3 className="font-head uppercase tracking-[0.2em] text-xs text-white/40 mb-3">{title}</h3>
      {lines.map((l, i) => <p key={i} className="text-white/80 text-lg leading-relaxed">{l}</p>)}
    </div>
  </div>
);
